import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CreatorInfo } from '@/components/kol/CreatorInfo';
import { StyledBadge } from '@/components/kol/StyledBadge';
import { PostingBarChart } from './PostingBarChart';
import { EngagementBarChart } from './EngagementBarChart';
import { AvgViewBarChart } from './AvgViewBarChart';
import { ExpenseBarChart } from './ExpenseBarChart';
import { CpvBarChart } from './CpvBarChart';
import PicDisplay from './PicDisplay';

interface KolAnalyticsHierarchicalData {
  kol_db_id: string;
  kol_name: string;
  social_media_handler?: string | null;
  platform?: string | null;
  profile_image_url?: string | null;
  content_count: number;
  total_expense: number;
  total_views: number;
  languages: string[];
  actual_cpv: number | string;
  avg_views_per_post: number | string;
  post_type?: string | null;
  type?: string | null;
  tier?: string | null;
  posting_year?: number | null;
  pic_id?: any;
  region?: string | null;
  earliest_post_date?: string | null;
  latest_post_date?: string | null;
  last_updated?: string | null;
  parent_id?: string | null;
  children?: KolAnalyticsHierarchicalData[];
}

interface Props {
  data: KolAnalyticsHierarchicalData;
  level: number;
  maxContentCount: number;
  maxTotalViews: number;
  maxAvgViews: number;
  maxExpense: number;
  maxCpv: number;
  isAnimating: boolean;
}

export const KolAnalyticsHierarchicalRow: React.FC<Props> = ({
  data,
  level,
  maxContentCount,
  maxTotalViews,
  maxAvgViews,
  maxExpense,
  maxCpv,
  isAnimating
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = data.children && data.children.length > 0;
  const isParent = !data.parent_id;
  const indent = level * 24;

  const toggleExpanded = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  // Calculate total values for parent (sum of all children + parent)
  const getTotalValues = () => {
    if (!hasChildren) {
      return {
        totalContentCount: data.content_count,
        totalViews: data.total_views,
        totalExpense: data.total_expense,
        avgViewsPerPost: data.avg_views_per_post,
        actualCpv: data.actual_cpv
      };
    }

    const childrenTotals = data.children!.reduce((acc, child) => ({
      contentCount: acc.contentCount + child.content_count,
      totalViews: acc.totalViews + child.total_views,
      totalExpense: acc.totalExpense + child.total_expense,
    }), { contentCount: 0, totalViews: 0, totalExpense: 0 });

    const totalContentCount = data.content_count + childrenTotals.contentCount;
    const totalViews = data.total_views + childrenTotals.totalViews;
    const totalExpense = data.total_expense + childrenTotals.totalExpense;
    const avgViewsPerPost = totalContentCount > 0 ? totalViews / totalContentCount : 0;
    const actualCpv = totalViews > 0 ? totalExpense / totalViews : 0;

    return {
      totalContentCount,
      totalViews,
      totalExpense,
      avgViewsPerPost,
      actualCpv
    };
  };

  const totalValues = getTotalValues();
  const isChildRow = level > 0;

  return (
    <>
      <tr 
        className={`border-b border-border/30 hover:bg-muted/30 transition-colors ${
          isChildRow ? 'bg-muted/10' : ''
        }`}
      >
        {/* Expand/Collapse column */}
        <td className="w-[40px] px-2 py-2" style={{ paddingLeft: `${12 + indent}px` }}>
          {hasChildren && (
            <button
              onClick={toggleExpanded}
              className="flex items-center justify-center w-5 h-5 rounded hover:bg-muted/50 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          )}
          {isChildRow && !hasChildren && (
            <div className="w-5 h-5 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-muted-foreground/30"></div>
            </div>
          )}
        </td>

        {/* KOL Name */}
        <td className="px-3 py-2 border-r border-border/30">
          <div className="flex items-center gap-2">
            {isChildRow && (
              <div className="flex items-center text-xs text-muted-foreground mr-1">
                <span className="w-3 border-t border-muted-foreground/30 mr-1"></span>
              </div>
            )}
            <CreatorInfo 
              name={data.kol_name} 
              language={data.languages && data.languages.length > 0 ? data.languages[0] : undefined} 
              handler={data.social_media_handler}
              platform={data.platform}
              profileImageUrl={data.profile_image_url}
              hideBadges={true}
            />
            {hasChildren && (
              <span className="text-xs text-muted-foreground ml-2 bg-muted/50 px-1.5 py-0.5 rounded">
                {data.children?.length} variants
              </span>
            )}
          </div>
        </td>

        {/* Languages */}
        <td className="px-3 py-2 border-r border-border/30">
          {data.languages && data.languages.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {data.languages.map((lang, index) => (
                <StyledBadge 
                  key={index} 
                  value={lang} 
                  className={isChildRow ? "text-xs px-1.5 py-0.5" : ""}
                />
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">No data</span>
          )}
        </td>

        {/* Tier */}
        <td className="px-3 py-2 border-r border-border/30">
          {data.tier ? (
            <span className={`${isChildRow ? 'text-sm' : ''}`}>{data.tier}</span>
          ) : (
            <span className="text-muted-foreground text-sm">No tier</span>
          )}
        </td>

        {/* Post Type */}
        <td className="px-3 py-2 border-r border-border/30">
          {data.post_type ? (
            <StyledBadge 
              value={data.post_type} 
              className={isChildRow ? "text-xs px-1.5 py-0.5" : ""}
            />
          ) : (
            <span className="text-muted-foreground text-sm">No type</span>
          )}
        </td>

        {/* PIC */}
        <td className="px-3 py-2 border-r border-border/30 min-w-[180px] w-[180px]">
          <PicDisplay picIds={data.pic_id} limit={2} />
        </td>

        {/* Total Posting */}
        <td className="px-3 py-2 border-r border-border/30">
          <PostingBarChart 
            value={hasChildren && !isExpanded ? totalValues.totalContentCount : data.content_count}
            maxValue={maxContentCount}
            className="min-w-[100px] h-6"
            shouldAnimate={isAnimating}
          />
        </td>

        {/* Total Engagements */}
        <td className="px-3 py-2 border-r border-border/30">
          <EngagementBarChart 
            value={hasChildren && !isExpanded ? totalValues.totalViews : data.total_views}
            maxValue={maxTotalViews}
            className="min-w-[100px] h-6"
            shouldAnimate={isAnimating}
          />
        </td>

        {/* Avg View/Post */}
        <td className="px-3 py-2 border-r border-border/30">
          <AvgViewBarChart 
            value={hasChildren && !isExpanded ? totalValues.avgViewsPerPost : data.avg_views_per_post}
            maxValue={maxAvgViews}
            className="min-w-[100px] h-6"
            shouldAnimate={isAnimating}
          />
        </td>

        {/* Total Expense */}
        <td className="px-3 py-2 border-r border-border/30">
          <ExpenseBarChart 
            value={hasChildren && !isExpanded ? totalValues.totalExpense : data.total_expense}
            maxValue={maxExpense}
            className="min-w-[100px] h-6"
            shouldAnimate={isAnimating}
          />
        </td>

        {/* Actual CPV */}
        <td className="px-3 py-2">
          <CpvBarChart 
            value={hasChildren && !isExpanded ? totalValues.actualCpv : data.actual_cpv}
            maxValue={maxCpv}
            className="min-w-[100px] h-6"
            shouldAnimate={isAnimating}
          />
        </td>
      </tr>

      {/* Render children if expanded */}
      {isExpanded && hasChildren && data.children?.map((child) => (
        <KolAnalyticsHierarchicalRow
          key={child.kol_db_id}
          data={child}
          level={level + 1}
          maxContentCount={maxContentCount}
          maxTotalViews={maxTotalViews}
          maxAvgViews={maxAvgViews}
          maxExpense={maxExpense}
          maxCpv={maxCpv}
          isAnimating={isAnimating}
        />
      ))}
    </>
  );
};
